import { useMemo } from 'react'
import { useAllPriceFeeds } from 'src/common/queries/usePriceFeeds/usePriceFeeds'
import { injectPricingData } from 'src/common/queries/usePriceFeeds/utils'
import { AnyToken } from 'src/common/types/models/Tokens'
import { TokenSelectorGroup } from '../components/modals/TokenMultiSelectorModal'
import { UseTokenSelectorTokensResult } from './types'
import { useDebouncedSearch } from './useDebouncedSearch'
import { useFilteredTokens } from './useFilteredTokens'

export const useTokenSelectorTokens = (
  group: TokenSelectorGroup,
  exclude?: AnyToken,
  excludeNFT?: boolean
): UseTokenSelectorTokensResult => {
  const { search, debouncedSearch, setSearch } = useDebouncedSearch()

  const {
    ownedTokens,
    notOwnedTokens,
    loading: filteredTokensLoading,
  } = useFilteredTokens(debouncedSearch, group, exclude)

  const { priceFeeds, isLoading: priceFeedsLoading, error } = useAllPriceFeeds()

  const filteredOwnedTokens = useMemo(() => {
    if (excludeNFT) {
      return ownedTokens?.filter?.(token => token.type === 'ERC20')
    }
    return ownedTokens
  }, [excludeNFT, ownedTokens])

  const filteredNotOwnedTokens = useMemo(() => {
    if (excludeNFT) {
      return notOwnedTokens?.filter?.(token => token.type === 'ERC20')
    }
    return notOwnedTokens
  }, [excludeNFT, notOwnedTokens])

  const extendedOwnedTokens = useMemo(() => {
    if (error || priceFeedsLoading) return filteredOwnedTokens
    return injectPricingData(filteredOwnedTokens, priceFeeds)
  }, [error, filteredOwnedTokens, priceFeeds, priceFeedsLoading])

  const extendedNotOwnedTokens = useMemo(() => {
    if (error || priceFeedsLoading) return filteredNotOwnedTokens
    return injectPricingData(filteredNotOwnedTokens, priceFeeds)
  }, [error, filteredNotOwnedTokens, priceFeeds, priceFeedsLoading])

  const loading = priceFeedsLoading || filteredTokensLoading

  return {
    ownedTokens: extendedOwnedTokens,
    notOwnedTokens: extendedNotOwnedTokens,
    loading,
    search,
    debouncedSearch,
    setSearch,
  }
}
